/**
 * Google Analytics 4 Event Tracking
 * Comprehensive tracking utilities for user interactions and conversions
 */

// Declare gtag function for TypeScript
declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
    dataLayer?: any[];
  }
}

/**
 * Track a custom event in Google Analytics 4
 * @param eventName - Name of the event (use snake_case for GA4)
 * @param params - Additional event parameters
 */
export const trackEvent = (eventName: string, params?: Record<string, any>) => {
  if (typeof window !== 'undefined' && window.gtag) {
    try {
      window.gtag('event', eventName, {
        timestamp: new Date().toISOString(),
        ...params,
      });
      
      // Debug mode (remove in production)
      if (import.meta.env.DEV) {
        console.log('📊 GA4 Event:', eventName, params);
      }
    } catch (error) {
      console.error('Analytics tracking error:', error);
    }
  }
};

/**
 * Track page view (for SPA routing)
 * @param path - Page path
 * @param title - Page title
 */
export const trackPageView = (path: string, title?: string) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'page_view', {
      page_path: path,
      page_title: title || document.title,
      page_location: window.location.href,
    });
  }
};

/**
 * Track calculator tab change
 */
export const trackTabChange = (tabName: string, previousTab?: string) => {
  trackEvent('calculator_tab_change', {
    calculator_type: tabName,
    previous_calculator: previousTab,
    engagement_type: 'navigation',
  });
};

/**
 * Track house loan calculation
 */
export const trackHouseLoanCalculation = (params: {
  loan_amount: number;
  interest_rate: number;
  loan_term_years: number;
  monthly_payment: number;
  total_interest: number;
}) => {
  trackEvent('house_loan_calculation', {
    calculator_type: 'house_loan',
    loan_amount_range: getBucketRange(params.loan_amount),
    interest_rate: params.interest_rate.toFixed(2),
    loan_term_years: params.loan_term_years,
    monthly_payment_range: getBucketRange(params.monthly_payment),
    total_interest_range: getBucketRange(params.total_interest),
    engagement_type: 'calculation',
  });
};

/**
 * Track partial repayment calculation
 */
export const trackPartialRepaymentCalculation = (params: {
  outstanding_balance: number;
  partial_repayment_amount: number;
  frequency: string;
  option_type: string;
}) => {
  trackEvent('partial_repayment_calculation', {
    calculator_type: 'partial_repayment',
    outstanding_balance_range: getBucketRange(params.outstanding_balance),
    partial_amount_range: getBucketRange(params.partial_repayment_amount),
    frequency: params.frequency,
    option_type: params.option_type,
    engagement_type: 'calculation',
  });
};

/**
 * Track PDF download
 */
export const trackPDFDownload = (params: {
  calculator_type: 'house_loan' | 'partial_repayment' | 'payment_schedule';
  language: string;
}) => {
  trackEvent('pdf_download', {
    calculator_type: params.calculator_type,
    language: params.language,
    content_type: 'pdf_report',
    engagement_type: 'download',
  });
};

/**
 * Track payment schedule view
 */
export const trackPaymentScheduleView = (scheduleType: string) => {
  trackEvent('view_payment_schedule', {
    calculator_type: scheduleType,
    content_type: 'payment_schedule',
    engagement_type: 'view',
  });
};

/**
 * Track blog post view
 */
export const trackBlogView = (params: {
  post_id: string;
  post_title: string;
  language: string;
}) => {
  trackEvent('blog_post_view', {
    post_id: params.post_id,
    post_title: params.post_title,
    language: params.language,
    content_type: 'blog',
    engagement_type: 'view',
  });
};

/**
 * Track blog post read time (call when user leaves or after threshold)
 */
export const trackBlogReadTime = (params: {
  post_id: string;
  read_time_seconds: number;
  scroll_depth_percent: number;
}) => {
  trackEvent('blog_engagement', {
    post_id: params.post_id,
    read_time_seconds: params.read_time_seconds,
    scroll_depth: `${params.scroll_depth_percent}%`,
    engagement_type: 'read',
  });
};

/**
 * Track affiliate link click
 */
export const trackAffiliateClick = (params: {
  affiliate_name: string;
  link_position: string;
  calculator_type?: string;
}) => {
  trackEvent('affiliate_click', {
    affiliate_name: params.affiliate_name,
    link_position: params.link_position,
    calculator_type: params.calculator_type || 'unknown',
    engagement_type: 'outbound_click',
  });
};

/**
 * Track language change
 */
export const trackLanguageChange = (newLanguage: string, previousLanguage: string) => {
  trackEvent('language_change', {
    new_language: newLanguage,
    previous_language: previousLanguage,
    engagement_type: 'setting_change',
  });
};

/**
 * Track form validation errors
 */
export const trackValidationError = (params: {
  calculator_type: string;
  field_name: string;
  error_type: string;
}) => {
  trackEvent('form_validation_error', {
    calculator_type: params.calculator_type,
    field_name: params.field_name,
    error_type: params.error_type,
  });
};

/**
 * Track error occurrence
 */
export const trackError = (errorType: string, errorMessage: string, errorContext?: string) => {
  trackEvent('error_occurred', {
    error_type: errorType,
    error_message: errorMessage.substring(0, 100), // Limit length
    error_context: errorContext,
    engagement_type: 'error',
  });
};

/**
 * Track conversion goal (e.g., completed calculation, downloaded PDF)
 */
export const trackConversion = (conversionType: string, value?: number) => {
  trackEvent('conversion', {
    conversion_type: conversionType,
    value: value || 1,
    currency: 'HKD',
  });
};

/**
 * Track user engagement time on page
 */
export const trackEngagementTime = (timeInSeconds: number, pageType: string) => {
  trackEvent('user_engagement', {
    engagement_time_seconds: timeInSeconds,
    page_type: pageType,
  });
};

/**
 * Helper: Convert amounts to privacy-friendly ranges for GDPR compliance
 */
function getBucketRange(amount: number): string {
  if (amount < 100000) return '0-100k';
  if (amount < 500000) return '100k-500k';
  if (amount < 1000000) return '500k-1M';
  if (amount < 2000000) return '1M-2M';
  if (amount < 5000000) return '2M-5M';
  if (amount < 10000000) return '5M-10M';
  return '10M+';
}

// Export all tracking functions
export default {
  trackEvent,
  trackPageView,
  trackTabChange,
  trackHouseLoanCalculation,
  trackPartialRepaymentCalculation,
  trackPDFDownload,
  trackPaymentScheduleView,
  trackBlogView,
  trackBlogReadTime,
  trackAffiliateClick,
  trackLanguageChange,
  trackValidationError,
  trackError,
  trackConversion,
  trackEngagementTime,
};
